import React from "react";

interface ErrorBoundaryState {
  error: Error | null;
}

// Without this, an uncaught render error unmounts the whole tree. In dev,
// CRA's react-error-overlay covers that with a red screen showing the
// stack trace — but a production build has no such overlay, so the exact
// same crash leaves a permanently blank page with nothing to go on unless
// someone happens to open devtools. This at least shows something, offers
// a way back, and surfaces the actual error message so it can be reported.
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("Unhandled error while rendering:", error, info.componentStack);
  }

  handleReload = () => {
    this.setState({ error: null });
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    if (error) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-950 px-6">
          <div className="text-center max-w-md">
            <p className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2">Something went wrong</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
              This page hit an unexpected error and couldn't finish rendering.
            </p>
            <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mb-6 break-words font-mono">{error.message}</p>
            <button
              onClick={this.handleReload}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
